import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { CREATE_CONVERSATION } from 'schema-v2/Chat/mutations';
import { CHANNEL_TYPE, UNIFIED_GRAPH } from 'utils/constants';
import { rawId } from 'utils/rawId';
import { goToConversation } from 'utils/routerHelpers';
import { failureToastMessage } from 'utils/toastHelpers';

import { useCallback, useState } from 'react';

import { useMutation } from '@apollo/client';

import { useCommunityContext } from 'components/CommunityContext';
import { useChatContext } from 'components/new/chat/providers/ChatProvider/ChatProvider';

export const useCreateConversation = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { currentUser, community } = useCommunityContext();
  const { session } = useChatContext();
  const [loadingConversationUserId, setLoadingConversationUserId] = useState('');

  const [createConversation] = useMutation(CREATE_CONVERSATION, {
    context: { clientName: UNIFIED_GRAPH },
  });

  const validateSession = useCallback(() => {
    if (!session) {
      failureToastMessage(
        t(
          'components-NewConversationModal-no_session',
          'Something went wrong. Please refresh the page to try again.'
        )
      );
      return false;
    }
    return true;
  }, [session, t]);

  const buildConversationInput = useCallback(
    (targetUserId: string) => ({
      conversationType: CHANNEL_TYPE,
      conversationUsers: [{ id: currentUser.gid }, { id: targetUserId }],
      metadata: { communityId: rawId(community.id)?.toString() },
    }),
    [currentUser.gid, community.id]
  );

  const handleUserErrors = useCallback(
    (userErrors: Array<{ code: string; conversationId?: string; message: string }>) => {
      for (const error of userErrors) {
        // Check if this is a conversation already exists error
        if (error.code === 'CONVERSATION_ALREADY_EXISTS' && error.conversationId) {
          // Return the existing conversation ID
          return error.conversationId;
        }
        failureToastMessage(error.message);
      }
      return null;
    },
    []
  );

  const handleGenericError = useCallback(() => {
    failureToastMessage(
      t('components-NewConversationModal-error', 'Something went wrong. Please try again.')
    );
    return null;
  }, [t]);

  const createConversationWithUser = useCallback(
    async (targetUserId: string) => {
      if (!validateSession()) {
        return null;
      }

      setLoadingConversationUserId(targetUserId);

      try {
        const {
          data: {
            createConversation: { userErrors, conversation },
          },
        } = await createConversation({
          variables: {
            input: buildConversationInput(targetUserId),
          },
        });

        if (userErrors?.length > 0) {
          return handleUserErrors(userErrors);
        }
        return conversation.id;
      } catch (error) {
        return handleGenericError();
      } finally {
        setLoadingConversationUserId('');
      }
    },
    [
      validateSession,
      buildConversationInput,
      createConversation,
      handleUserErrors,
      handleGenericError,
    ]
  );

  const navigateToConversation = useCallback(
    (conversationId: string) => {
      goToConversation(router, community.id, conversationId);
    },
    [community.id, router]
  );

  return {
    createConversationWithUser,
    navigateToConversation,
    loadingConversationUserId,
  };
};
